import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { mcpService } from '../../services/mcpService';
import { getErrorMessage } from '../../utils/error';
import { useAuthStore } from '../../store/authStore';
import { OtpInput } from '../../components/common/OtpInput';
import { useLanguage } from '../../features/language/useLanguage';

const RESEND_COOLDOWN_SECONDS = 60;

interface ResendErrorResponse {
  response?: {
    status?: number;
    data?: {
      error?: string;
      retry_after?: number;
    };
  };
}

const buildVerifySchema = (t: (key: string, params?: Record<string, string | number>, fallback?: string) => string) =>
  z.object({
    otp: z.string().length(6, t('auth.validation.otpLength')),
  });

type VerifyFormData = z.infer<ReturnType<typeof buildVerifySchema>>;

interface LocationState {
  registrationData?: unknown;
}

export const VerifyOtpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const verifySchema = useMemo(() => buildVerifySchema(t), [t]);
  const setAuth = useAuthStore((state) => state.login);
  const state = location.state as LocationState;
  const registrationData = state?.registrationData as { email: string; [key: string]: unknown } | undefined;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      otp: '',
    },
  });

  const [resendSecondsLeft, setResendSecondsLeft] = useState<number>(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendNotice, setResendNotice] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (!registrationData) {
      navigate('/register');
    }
  }, [registrationData, navigate]);

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const interval = window.setInterval(() => {
      setResendSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [resendSecondsLeft]);

  const handleResend = useCallback(async () => {
    if (resendSecondsLeft > 0 || isResending || !registrationData?.email) return;
    setIsResending(true);
    setResendNotice(null);
    try {
      await authService.requestOtp(registrationData.email);
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      setResendNotice({ kind: 'success', message: t('auth.verifyOtp.resendSuccess') });
    } catch (error) {
      const axiosError = error as ResendErrorResponse;
      // Backend enforces a 60s cooldown too. If the server rejects with 429,
      // sync the local timer with the server's retry_after to stay honest.
      if (axiosError?.response?.status === 429) {
        const retryAfter = axiosError.response.data?.retry_after ?? RESEND_COOLDOWN_SECONDS;
        setResendSecondsLeft(retryAfter);
        setResendNotice({
          kind: 'error',
          message: t('auth.verifyOtp.resendCooldown', { seconds: retryAfter }),
        });
      } else {
        setResendNotice({ kind: 'error', message: getErrorMessage(error) });
      }
    } finally {
      setIsResending(false);
    }
  }, [resendSecondsLeft, isResending, registrationData, t]);

  const onSubmit = async (data: VerifyFormData) => {
    try {
      await authService.verifyOtp(registrationData?.email || '', data.otp);

      const response = await authService.register(registrationData as Record<string, unknown>);

      // Map backend user to store user
      const user = {
        id: response.user.id,
        email: response.user.email,
        name: response.user.full_name,
        role: response.user.role,
        companyId: response.user.company_id,
        emailNotificationsEnabled: response.user.email_notifications_enabled ?? false
      };

      const company = {
        id: response.company.id,
        name: response.company.name,
        code: response.company.code,
        address: response.company.address
      };

      setAuth(user, company, response.token);

      // Auto-connect MCP key if generated during registration
      if (response.mcp_api_key) {
        mcpService.setActiveKey(response.mcp_api_key);
      }

      navigate('/');
    } catch (error) {
      console.error('Registration failed:', error);
      // Backend returns typed voucher errors as { error: 'voucher.<kind>' } —
      // translate those into the user-facing register-page messages so a bad
      // voucher code at OTP-time gets a clear, fixable error.
      const apiError = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      const code = apiError?.error;
      let message = getErrorMessage(error);
      switch (code) {
        case 'voucher.not_found':
          message = t('auth.register.voucherNotFound');
          break;
        case 'voucher.expired':
          message = t('auth.register.voucherExpired');
          break;
        case 'voucher.exhausted':
          message = t('auth.register.voucherExhausted');
          break;
        case 'voucher.deleted':
          message = t('auth.register.voucherInvalid');
          break;
      }
      setError('root', { message });
    }
  };

  if (!registrationData) {
    return null;
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          {t('auth.verifyOtp.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.verifyOtp.subtitle', { email: registrationData.email })}
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errors.root.message}
          </div>
        )}

        <div>
          <label htmlFor="otp" className="block text-sm font-medium text-gray-700 text-center mb-4">
            {t('auth.verifyOtp.verificationCode')}
          </label>
          <div className="mt-1">
            <Controller
              control={control}
              name="otp"
              render={({ field: { onChange, value } }) => (
                <OtpInput
                  value={value}
                  onChange={onChange}
                  disabled={isSubmitting}
                  dataTestId="verify-otp-input"
                />
              )}
            />
            {errors.otp && (
              <p className="mt-2 text-sm text-red-600 text-center">{errors.otp.message}</p>
            )}
          </div>

          <div className="mt-5 flex flex-col items-center gap-1">
            <p className="text-sm text-gray-600">{t('auth.verifyOtp.resendPrompt')}</p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendSecondsLeft > 0 || isResending}
              data-test-id="verify-otp-resend-button"
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:text-gray-400"
            >
              {isResending
                ? t('auth.verifyOtp.resending')
                : resendSecondsLeft > 0
                  ? t('auth.verifyOtp.resendIn', { seconds: resendSecondsLeft })
                  : t('auth.verifyOtp.resend')}
            </button>
            {resendNotice && (
              <p
                role="status"
                data-test-id="verify-otp-resend-notice"
                className={`mt-1 text-xs text-center ${
                  resendNotice.kind === 'success' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {resendNotice.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            data-test-id="verify-submit-button"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {t('auth.verifyOtp.submit')}
          </button>
        </div>
      </form>
    </>
  );
};
